/**
 * 내 팀 조회 관련 함수들
 */

import { createBrowserClient } from '@/supabase'
import { getAuthenticatedUserAndProfile, getTeamMemberAccountsBatch, TEAM_MEMBER_STATUS } from '@/utils/team-helpers'
import type { Team, MyTeam } from '../team-types'

/**
 * 사용자가 속한 모든 팀 목록 조회 (매니저인 팀 + 팀원으로 속한 팀)
 */
export const fetchMyTeams = async () => {
  try {
    const { supabase, profileId } = await getAuthenticatedUserAndProfile()

    // 1. 매니저인 팀 조회 (삭제되지 않은 팀만)
    const { data: managedTeams, error: managedError } = await supabase
      .from('teams')
      .select('*')
      .eq('manager_profile_id', profileId)
      .is('deleted_at', null)

    if (managedError) {
      return { data: [], error: managedError }
    }

    const managedTeamsTyped = (managedTeams || []) as unknown as Team[]

    // 2. 팀원으로 속한 팀 조회
    const { data: memberTeams, error: memberError } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('profile_id', profileId)
      .eq('status', TEAM_MEMBER_STATUS.ACTIVE)

    if (memberError) {
      return { data: [], error: memberError }
    }

    // 팀원으로 속한 팀들의 상세 정보 조회 (삭제되지 않은 팀만)
    interface MemberTeamRow {
      team_id: number | null
    }
    const memberTeamIds = ((memberTeams || []) as unknown as MemberTeamRow[])
      .map((m) => m.team_id)
      .filter((id): id is number => typeof id === 'number')
    let memberTeamDetails: Team[] = []

    if (memberTeamIds.length > 0) {
      const { data, error: teamDetailsError } = await supabase
        .from('teams')
        .select('*')
        .in('id', memberTeamIds)
        .is('deleted_at', null)

      if (teamDetailsError) {
        return { data: [], error: teamDetailsError }
      }
      memberTeamDetails = (data || []) as unknown as Team[]
    }

    // 모든 팀 ID 수집 (중복 제거)
    const allTeamIds = [
      ...managedTeamsTyped.map((t) => t.id),
      ...memberTeamDetails.map((t) => t.id),
    ]
    const uniqueTeamIds = Array.from(new Set(allTeamIds))

    if (uniqueTeamIds.length === 0) {
      return { data: [], error: null }
    }

    // 3. 모든 팀의 팀원 정보를 한 번에 조회 (성능 최적화)
    const { data: allTeamMembers, error: membersError } = await supabase
      .from('team_members')
      .select('*')
      .in('team_id', uniqueTeamIds)
      .eq('status', TEAM_MEMBER_STATUS.ACTIVE)

    if (membersError) {
      return { data: [], error: membersError }
    }

    interface TeamMemberRow {
      id: number
      team_id: number | null
      profile_id: string | null
      status: string | null
      [key: string]: any
    }
    const allTeamMembersTyped = (allTeamMembers || []) as unknown as TeamMemberRow[]

    // 4. 모든 매니저와 팀원의 profile_id 수집
    const allManagerProfileIds = [
      ...managedTeamsTyped.map((t) => t.manager_profile_id),
      ...memberTeamDetails.map((t) => t.manager_profile_id),
    ].filter(Boolean) as string[]

    const allMemberProfileIds = allTeamMembersTyped
      .map((m) => m.profile_id)
      .filter(Boolean) as string[]

    const allProfileIds = Array.from(new Set([...allManagerProfileIds, ...allMemberProfileIds]))

    // 5. 모든 계정 정보를 한 번에 조회 (성능 최적화)
    const accountsMap = await getTeamMemberAccountsBatch(supabase as any, allProfileIds)

    // 6. 팀별 팀원 그룹화
    const membersByTeamId = new Map<number, TeamMemberRow[]>()
    for (const member of allTeamMembersTyped) {
      const teamId = member.team_id
      if (teamId && !membersByTeamId.has(teamId)) {
        membersByTeamId.set(teamId, [])
      }
      if (teamId) {
        membersByTeamId.get(teamId)!.push(member)
      }
    }

    // 7. 매니저인 팀들 보강
    const enrichedManagedTeams = managedTeamsTyped
      .map((team) => {
        const teamMembers = membersByTeamId.get(team.id) || []

        const enrichedMembers = teamMembers.map((member) => ({
          ...member,
          account: member.profile_id ? accountsMap.get(member.profile_id) || null : null,
        }))

        return {
          ...team,
          isManager: true,
          manager: team.manager_profile_id
            ? accountsMap.get(team.manager_profile_id) || null
            : null,
          team_members: enrichedMembers,
        }
      })

    // 8. 팀원으로 속한 팀들 보강
    const enrichedMemberTeams = memberTeamDetails
      .map((team) => {
        const teamMembers = membersByTeamId.get(team.id) || []

        const enrichedMembers = teamMembers.map((member) => ({
          ...member,
          account: member.profile_id ? accountsMap.get(member.profile_id) || null : null,
        }))

        return {
          ...team,
          isManager: false,
          manager: team.manager_profile_id
            ? accountsMap.get(team.manager_profile_id) || null
            : null,
          team_members: enrichedMembers,
        }
      })

    // 9. 두 결과를 합치고 중복 제거
    interface EnrichedTeamMember {
      account: {
        profile_id: string
        user_id: string
        username: string
        bio?: string | null
      } | null
      id: number
      team_id: number | null
      profile_id: string | null
      status: string | null
      [key: string]: any
    }

    interface EnrichedTeam extends Team {
      isManager: boolean
      manager: {
        profile_id: string
        user_id: string
        username: string
        bio?: string | null
      } | null
      team_members: EnrichedTeamMember[]
    }

    const allTeams: EnrichedTeam[] = []
    const teamIdSet = new Set<number>()

    // 매니저인 팀 추가
    enrichedManagedTeams.forEach((team) => {
      if (team.id && !teamIdSet.has(team.id)) {
        teamIdSet.add(team.id)
        allTeams.push(team as EnrichedTeam)
      }
    })

    // 팀원으로 속한 팀 추가
    enrichedMemberTeams.forEach((team) => {
      if (team.id && !teamIdSet.has(team.id)) {
        teamIdSet.add(team.id)
        allTeams.push(team as EnrichedTeam)
      }
    })

    return { data: allTeams, error: null }
  } catch (error) {
    return {
      data: [],
      error: error instanceof Error ? error : new Error('프리랜서 프로필을 찾을 수 없습니다.'),
    }
  }
}

/**
 * 현재 profile이 속한 모든 팀과 각 팀에서의 역할 조회
 */
export const fetchMyTeamsWithRole = async (currentProfileId: string): Promise<MyTeam[]> => {
  const supabase = createBrowserClient()

  // 1) 매니저로 있는 팀
  const { data: managerTeams, error: managerError } = await supabase
    .from('teams')
    .select('id, name, bio, created_at, updated_at')
    .eq('manager_profile_id', currentProfileId)
    .is('deleted_at', null)

  if (managerError) throw managerError

  interface TeamBasicInfo {
    id: number
    name: string
    bio?: string | null
    created_at?: string | null
    updated_at?: string | null
  }

  const managerTeamsTyped = (managerTeams ?? []) as unknown as TeamBasicInfo[]

  // 2) 메이커(팀원)으로 속한 팀
  const { data: memberRows, error: memberError } = await supabase
    .from('team_members')
    .select('team_id')
    .eq('profile_id', currentProfileId)
    .eq('status', TEAM_MEMBER_STATUS.ACTIVE)

  if (memberError) throw memberError

  interface TeamRow {
    team_id: number | null
  }

  const memberTeamIds = Array.from(
    new Set(
      ((memberRows ?? []) as unknown as TeamRow[])
        .map((r) => r.team_id)
        .filter((id): id is number => id !== null && id !== undefined),
    ),
  )

  let memberTeams: TeamBasicInfo[] = []
  if (memberTeamIds.length > 0) {
    const { data, error } = await supabase
      .from('teams')
      .select('id, name, bio, created_at, updated_at')
      .in('id', memberTeamIds)
      .is('deleted_at', null)

    if (error) throw error
    memberTeams = (data ?? []) as unknown as TeamBasicInfo[]
  }

  // 3) 결과 병합 (role 붙이기)
  const result: MyTeam[] = [
    ...managerTeamsTyped.map((t) => ({
      team: t,
      role: 'MANAGER' as const,
    })),
    ...memberTeams
      .filter((t) => !managerTeamsTyped.some((mt) => mt.id === t.id)) // 중복 제거
      .map((t) => ({
        team: t,
        role: 'MAKER' as const,
      })),
  ]

  return result
}
